import type {
  AccessEntry,
  AssetTree,
  EnsureWellsRequest,
  EnsureWellsResponse,
  Equipment,
  EquipmentInput,
  EquipmentType,
  Field,
  FieldInput,
  FlowPeriodInput,
  GrantNode,
  ImportResult,
  OkResponse,
  SeqLogEntry,
  SeqLogInput,
  TestInput,
  Well,
  WellInput,
  WellTest,
  WellSupervisorsRequest,
  WellSupervisorsResponse,
} from '@twinlink/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '@web/lib/api';
import { onAlertsChanged } from '@web/lib/live';

const base = (orgId: string) => `/orgs/${orgId}`;
export const assetKeys = {
  all: (orgId: string) => ['org', orgId, 'assets'] as const,
  tree: (orgId: string) => ['org', orgId, 'assets', 'tree'] as const,
  one: (orgId: string, kind: string, id: string) => ['org', orgId, 'assets', kind, id] as const,
  list: (orgId: string, kind: string, filter?: string) => ['org', orgId, 'assets', kind, 'list', filter ?? ''] as const,
};

/** Every asset mutation refreshes all asset queries of the org (tree, lists, details). */
function useInvalidateAssets(orgId: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: assetKeys.all(orgId) });
}

export const useAssetTree = (orgId: string, enabled = true) =>
  useQuery({ queryKey: assetKeys.tree(orgId), queryFn: () => api<AssetTree>(`${base(orgId)}/tree`), enabled, staleTime: 30_000 });

const one = <T,>(kind: string, path: string) => (orgId: string, id: string | undefined) =>
   
  useQuery({ queryKey: assetKeys.one(orgId, kind, id ?? ''), queryFn: () => api<T>(`${base(orgId)}/${path}/${id}`), enabled: !!id, retry: false });

export const useField = one<Field>('field', 'fields');
export const useWell = one<Well>('well', 'wells');
export const useTest = one<WellTest>('test', 'tests');

export const useTests = (orgId: string, wellId?: string) =>
  useQuery({
    queryKey: assetKeys.list(orgId, 'tests', wellId),
    queryFn: () => api<{ items: WellTest[] }>(`${base(orgId)}/tests`, { query: { wellId } }).then((r) => r.items),
  });

export const useEquipment = (orgId: string, wellId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: assetKeys.list(orgId, 'equipment', wellId),
    queryFn: () => api<{ items: Equipment[] }>(`${base(orgId)}/equipment`, { query: { wellId } }).then((r) => r.items),
    enabled,
  });

export const useEquipmentTypes = (orgId: string, enabled = true) =>
  useQuery({
    queryKey: ['org', orgId, 'equipment-types'],
    queryFn: () => api<{ items: EquipmentType[] }>(`${base(orgId)}/equipment-types`).then((r) => r.items),
    enabled,
    staleTime: 300_000,
  });

/**
 * The sequence log of a test. Refreshed when the server says something changed: a supervisor's
 * WhatsApp answer fills an event (doc 14), and open pages show it without a reload.
 */
export function useSeqLog(orgId: string, testId: string | undefined) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!testId) return;
    return onAlertsChanged(orgId, () => void qc.invalidateQueries({ queryKey: assetKeys.list(orgId, 'seqlog', testId) }));
  }, [orgId, testId, qc]);
  return useQuery({
    queryKey: assetKeys.list(orgId, 'seqlog', testId),
    queryFn: () => api<{ items: SeqLogEntry[] }>(`${base(orgId)}/tests/${testId}/seqlog`).then((r) => r.items),
    enabled: !!testId,
  });
}

/** Who is asked on WhatsApp when something happens on the well (doc 14 §2.2). */
export const useWellSupervisors = (orgId: string, wellId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: assetKeys.one(orgId, 'supervisors', wellId ?? ''),
    queryFn: () => api<WellSupervisorsResponse>(`${base(orgId)}/wells/${wellId}/supervisors`),
    enabled: enabled && !!wellId,
  });

export function useSaveWellSupervisors(orgId: string, wellId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: WellSupervisorsRequest) => api<WellSupervisorsResponse>(`${base(orgId)}/wells/${wellId}/supervisors`, { method: 'PUT', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: assetKeys.one(orgId, 'supervisors', wellId) }),
  });
}

type Kind = 'fields' | 'wells' | 'tests' | 'equipment';
type InputOf<K extends Kind> = K extends 'fields' ? FieldInput : K extends 'wells' ? WellInput : K extends 'tests' ? TestInput : EquipmentInput;

/** Create (no id) or update (id) an asset of the given kind. */
export function useSaveAsset<K extends Kind>(orgId: string, kind: K) {
  const invalidate = useInvalidateAssets(orgId);
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Partial<InputOf<K>> }) =>
      api<{ id: string }>(`${base(orgId)}/${kind}${id ? `/${id}` : ''}`, { method: id ? 'PATCH' : 'POST', body }),
    onSuccess: invalidate,
  });
}

/** Finds wells by field and well name, creating the missing ones (signal import). */
export function useEnsureWells(orgId: string) {
  const invalidate = useInvalidateAssets(orgId);
  return useMutation({
    mutationFn: (wells: EnsureWellsRequest['wells']) => api<EnsureWellsResponse>(`${base(orgId)}/wells/ensure`, { method: 'POST', body: { wells } }),
    onSuccess: invalidate,
  });
}

export function useDeleteAsset(orgId: string, kind: Kind) {
  const invalidate = useInvalidateAssets(orgId);
  return useMutation({
    mutationFn: (id: string) => api<OkResponse>(`${base(orgId)}/${kind}/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useSaveFlowPeriod(orgId: string, testId: string) {
  const invalidate = useInvalidateAssets(orgId);
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: FlowPeriodInput }) =>
      api<WellTest>(`${base(orgId)}/tests/${testId}/flow-periods${id ? `/${id}` : ''}`, { method: id ? 'PATCH' : 'POST', body }),
    onSuccess: invalidate,
  });
}

export function useDeleteFlowPeriod(orgId: string, testId: string) {
  const invalidate = useInvalidateAssets(orgId);
  return useMutation({
    mutationFn: (id: string) => api<WellTest>(`${base(orgId)}/tests/${testId}/flow-periods/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useAddSeqLog(orgId: string, testId: string) {
  const invalidate = useInvalidateAssets(orgId);
  return useMutation({
    mutationFn: (body: SeqLogInput) => api<SeqLogEntry>(`${base(orgId)}/tests/${testId}/seqlog`, { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

export const useWhoCanSee = (orgId: string, node: GrantNode | null) =>
  useQuery({
    queryKey: ['org', orgId, 'access', node?.nodeType, node?.nodeId],
    queryFn: () => api<{ items: AccessEntry[] }>(`${base(orgId)}/access`, { query: { ...node } }).then((r) => r.items),
    enabled: !!node,
  });

export const useMemberGrants = (orgId: string, userId: string | undefined) =>
  useQuery({
    queryKey: ['org', orgId, 'grants', userId],
    queryFn: () => api<{ grants: GrantNode[] }>(`${base(orgId)}/members/${userId}/grants`).then((r) => r.grants),
    enabled: !!userId,
  });

export function usePutGrants(orgId: string, userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { grants: GrantNode[]; confirmClientFieldGrant?: boolean }) =>
      api<{ grants: GrantNode[] }>(`${base(orgId)}/members/${userId}/grants`, { method: 'PUT', body }),
    onSuccess: (data) => {
      qc.setQueryData(['org', orgId, 'grants', userId], data.grants);
      void qc.invalidateQueries({ queryKey: ['org', orgId, 'access'] });
    },
  });
}

export function useCsvImport(orgId: string, target: 'assets' | 'members' | 'signals') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ csv, dryRun }: { csv: string; dryRun: boolean }) =>
      api<ImportResult>(`${base(orgId)}/${target}/import`, { method: 'POST', body: { csv }, query: { dryRun } }),
    onSuccess: (res) => {
      if (!res.dryRun) void qc.invalidateQueries({ queryKey: ['org', orgId] });
    },
  });
}
